import { accountingType } from '@/features/master-table/models';
import { useCurrentFiscalYear } from '@/features/search/_hooks/get-current-fiscal-year';
import { initialValueOfAnalysisSearchForm } from '@/features/search/_models/analysis-search';
import { SEARCH_DEBOUNCE_MS_TIME } from '@/features/search/_utils/constants';
import { useDebouncedQueryStates } from '@/lib/nuqs';
import {
  aggregationTargetTypeEnum,
  aggregationTypeEnum,
  analysisAxisEnum,
  analysisTargetEnum,
  implementationMethodEnum,
  projectCategoryEnum,
} from '@/utils/enum';
import {
  parseAsArrayOf,
  parseAsBoolean,
  parseAsInteger,
  parseAsString,
  parseAsStringLiteral,
} from 'nuqs';
import { graphTypeEnum } from '../_models/project';

export const useAnalysisPageSearchQueryStates = ({
  useDebounceControl = false,
}: {
  useDebounceControl?: boolean;
} = {}) => {
  const currentFiscalYear = useCurrentFiscalYear();
  const year = currentFiscalYear.data ?? currentFiscalYear.basicFiscalYear;
  const initialValue = initialValueOfAnalysisSearchForm(year);
  const queryStates = useDebouncedQueryStates({
    keyMap: {
      page: parseAsInteger.withDefault(1),
      pageSize: parseAsInteger.withDefault(30),
      analysisTarget: parseAsStringLiteral(
        analysisTargetEnum.options,
      ).withDefault(initialValue.analysisTarget),
      fiscalYearRange: parseAsArrayOf(parseAsInteger).withDefault(
        initialValue.fiscalYearRange,
      ),
      q: parseAsString,
      isSearchTargetProjectName: parseAsBoolean.withDefault(false),
      cascader: parseAsString,
      corporateNumber: parseAsString,
      categories: parseAsArrayOf(
        parseAsStringLiteral(projectCategoryEnum.options),
      ),
      implementationMethods: parseAsArrayOf(
        parseAsStringLiteral(implementationMethodEnum.options),
      ),
      accountingType: parseAsStringLiteral(accountingType.options),
      accountingTypeIds: parseAsArrayOf(parseAsString),
      projectNumbers: parseAsString,
      // // 事業年度（作成年度）
      startYear: parseAsInteger,
      endYear: parseAsInteger,
      // PS用
      organizationIds: parseAsArrayOf(parseAsString),
      otherResponsibleOrganizationIds: parseAsArrayOf(parseAsString),
      policyIds: parseAsArrayOf(parseAsString),
      expenditureBudgetPurposeIds: parseAsArrayOf(parseAsString),
      aggregationType: parseAsStringLiteral(
        aggregationTypeEnum.options,
      ).withDefault(initialValue.aggregationType),
      aggregationTargetType: parseAsStringLiteral(
        aggregationTargetTypeEnum.options,
      ).withDefault(initialValue.aggregationTargetType),
      analysisAxis: parseAsStringLiteral(analysisAxisEnum.options).withDefault(
        initialValue.analysisAxis,
      ),
      /**
       * ソート順と年度を組み合わせた文字列
       * 例: "descend_2023" | "ascend_2023" | null
       * @see SortOrder from antd Table - "descend" | "ascend" | null
       */
      order: parseAsString,
      graphType: parseAsStringLiteral(graphTypeEnum.options).withDefault(
        graphTypeEnum.Values.stackedVerticalBar,
      ),
    },
    debounceMs: SEARCH_DEBOUNCE_MS_TIME,
    useDebounceControl,
  });
  return queryStates;
};

export type AnalysisPageSearchQueryStates = ReturnType<
  typeof useAnalysisPageSearchQueryStates
>['searchQueryStates'];
